/**
 * HTTP Client with Enhanced Reliability
 *
 * Features:
 * - Token caching to reduce network overhead
 * - Exponential backoff retry for transient failures (network errors and 5xx
 *   only -- a 429 is surfaced, never retried; see isRetryableError)
 * - A shorter total budget for requests a person is waiting on, so a button
 *   never leaves a customer on "Sending..." for two minutes (interactiveRequest)
 * - Circuit breaker pattern for failing services
 * - Guest checkout support (no redirect on public paths)
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 */

import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import configuration from "@/config/configuration";
import { logger } from "@/lib/logger";
import {
  announceRenewalUnavailable,
  tokenCache,
  fetchSession,
} from "@/lib/token-cache";
import { detectLanguage, redirectToAuthLogin } from "@/lib/auth-redirect";
import { mayRetryAutomatically } from "@/lib/retry-policy";

export { mayRetryAutomatically };

// Re-export fetchSession for backward compatibility
export { fetchSession };

declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * A-5 (S620): true when a person is watching this request finish -- a button
     * press, a form submit. Such a request gets the shorter interactive budget
     * (see INTERACTIVE_TIMEOUT_MS / INTERACTIVE_TOTAL_BUDGET_MS) instead of the
     * default one, which is sized for background work. Set it with
     * interactiveRequest(), not by hand.
     */
    interactive?: boolean;
    /**
     * N-8 (FCR S732): allow the automatic retry for a request whose method is
     * not idempotent (POST, PATCH). Only for a request that is safe to send
     * twice -- today, creating a checkout, which ends the customer's own earlier
     * unpaid session before building the next.
     */
    retryUnsafe?: boolean;
    /**
     * SU-5 (FCR S732): at most this many automatic retries (default 3). A caller
     * that runs its own bounded retry loop sets 0 so the two cannot multiply.
     */
    maxRetries?: number;
  }
}

/**
 * The per-request state the retry logic keeps. `_startedAt` is stamped once, on
 * the first attempt, and survives every retry because the same config object is
 * handed back to axios.
 */
type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _retryCount?: number;
  _startedAt?: number;
};

// ============================================================================
// Public Path Detection (Guest Checkout Support)
// ============================================================================

/**
 * Public paths that should NOT trigger login redirect on 401
 * These paths support guest checkout flow
 */
const PUBLIC_PATHS = [
  "/cart",
  "/add-to-cart",
  "/Basket",
  "/checkout",
  "/return",
  "/thanks",
  "/list",
  "/offline",
  "/address",
  // Unsubscribe pages — loaded from email links by unauthenticated users.
  // Must not redirect to login on any API error.
  "/unsubscribe-cart",
  "/migration/unsubscribe",
  "/trial/unsubscribed",
  // Trial registration pages — publicly accessible from WebView
  "/trial/register",
  "/trial/verify",
  "/trial/status",
];

/**
 * Check if current path is a public path that supports guest access
 */
const isPublicPath = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_PATHS.some(
    (publicPath) =>
      pathname.includes(publicPath) ||
      pathname.endsWith(publicPath) ||
      // Handle locale prefix (e.g., /en/cart)
      pathname.match(new RegExp(`^/[a-z]{2}${publicPath}($|/|\\?)`)),
  );
};

// ============================================================================
// Circuit Breaker
// ============================================================================

interface CircuitState {
  failures: number;
  lastFailure: number;
  isOpen: boolean;
}

const circuitBreakers = new Map<string, CircuitState>();
const CIRCUIT_FAILURE_THRESHOLD = 5;
const CIRCUIT_RESET_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Get or create circuit state for a service
 */
const getCircuitState = (service: string): CircuitState => {
  if (!circuitBreakers.has(service)) {
    circuitBreakers.set(service, {
      failures: 0,
      lastFailure: 0,
      isOpen: false,
    });
  }
  return circuitBreakers.get(service)!;
};

/**
 * Check if circuit is open (failing)
 */
const isCircuitOpen = (service: string): boolean => {
  const state = getCircuitState(service);

  if (!state.isOpen) return false;

  // Check if reset timeout has passed
  if (Date.now() - state.lastFailure > CIRCUIT_RESET_TIMEOUT_MS) {
    // Half-open: allow one request through
    state.isOpen = false;
    state.failures = 0;
    return false;
  }

  return true;
};

/**
 * Record a failure for circuit breaker
 */
const recordFailure = (service: string): void => {
  const state = getCircuitState(service);
  state.failures++;
  state.lastFailure = Date.now();

  if (state.failures >= CIRCUIT_FAILURE_THRESHOLD) {
    state.isOpen = true;
    logger.warn(`Circuit breaker opened for service: ${service}`);
  }
};

/**
 * Record a success (reset failures)
 */
const recordSuccess = (service: string): void => {
  const state = getCircuitState(service);
  state.failures = 0;
  state.isOpen = false;
};

// ============================================================================
// Retry Logic
// ============================================================================

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

/** The default budget: 30s timeout x 4 attempts plus back-off, ~2 minutes. */
export const DEFAULT_TIMEOUT_MS = 30000;

/**
 * A-5 (S620): the budget for a request a person is watching.
 *
 * A background refresh may take two minutes; a customer holding down a button
 * that says "Sending..." may not. Anything beyond about fifteen seconds with no
 * answer is read as broken, and the customer clicks again or leaves. These two
 * numbers together cap an interactive request at roughly that:
 *
 *   attempt 1 (<=10s) + back-off (~1-2s) + attempt 2 (<=10s), stopped by the
 *   total budget the moment the next attempt could not finish inside it.
 *
 * They are named and exported so the number is changed here, once, rather than
 * appearing as a literal at a call site.
 */
export const INTERACTIVE_TIMEOUT_MS = 10000;
export const INTERACTIVE_TOTAL_BUDGET_MS = 15000;

/**
 * Mark a request as one a person is waiting on, so it gets the interactive
 * budget above instead of the default two-minute one.
 *
 *   await api.post("/users/request-password-reset", body, interactiveRequest());
 *
 * A caller may override anything except the flag itself:
 *
 *   interactiveRequest({ timeout: 5000 })
 */
export const interactiveRequest = (
  config: AxiosRequestConfig = {},
): AxiosRequestConfig => ({
  timeout: INTERACTIVE_TIMEOUT_MS,
  ...config,
  interactive: true,
});

/**
 * Check if error is retryable
 */
const isRetryableError = (error: AxiosError): boolean => {
  // Network errors
  if (!error.response) return true;

  // 5xx server errors (except 501 Not Implemented)
  const status = error.response.status;
  if (status >= 500 && status !== 501) return true;

  // A-5 (S620): 429 Too Many Requests is deliberately NOT retryable.
  //
  // The server has already said the caller is going too fast, and it says when
  // to come back in Retry-After. Retrying it blind adds to the load that caused
  // the limit, can push the caller further down the limiter's window, and
  // extends a wait that was already up to two minutes. The right answer is to
  // surface the server's response so the page can show what it said.
  //
  // If a request ever does need to honour Retry-After, that belongs here as a
  // scheduled single retry against the header value -- not as a blind loop.

  return false;
};

/**
 * Calculate retry delay with exponential backoff and jitter
 */
const getRetryDelay = (attempt: number): number => {
  const exponentialDelay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return exponentialDelay + jitter;
};

/**
 * A-5: has this request spent the budget a watching customer will tolerate?
 *
 * Measured from when the FIRST attempt left, not from the last failure, so the
 * cap covers the whole chain. `delay` is the pause the next retry would take:
 * a retry that could not even start inside the budget is not worth starting.
 */
const isBudgetExhausted = (
  config: RetryableRequestConfig,
  delay: number,
): boolean => {
  if (!config.interactive) return false;
  const startedAt = config._startedAt ?? Date.now();
  return Date.now() - startedAt + delay >= INTERACTIVE_TOTAL_BUDGET_MS;
};

// ============================================================================
// Axios Instance
// ============================================================================

export const api = axios.create({
  baseURL: configuration.backend_url + "/api/v1",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // A-5: the default, for background work. A request a person is waiting on
  // should be issued with interactiveRequest(), which lowers this.
  timeout: DEFAULT_TIMEOUT_MS,
});

// ============================================================================
// Request Interceptor
// ============================================================================

api.interceptors.request.use(
  async (config) => {
    // A-5: stamp the first attempt only. Retries re-enter this interceptor with
    // the same config object, so the total budget is measured across the whole
    // chain rather than being reset by every failure.
    const retryable = config as RetryableRequestConfig;
    retryable._startedAt ??= Date.now();

    // Check circuit breaker
    const service = config.baseURL
      ? new URL(config.baseURL).hostname
      : "unknown";
    if (isCircuitOpen(service)) {
      logger.warn(`Circuit breaker is open for ${service}, rejecting request`);
      return Promise.reject(
        new Error(`Service temporarily unavailable: ${service}`),
      );
    }

    // A-1 (S703): the dashboard language travels with EVERY shop API call.
    //
    // Before this, it reached the server only where somebody had remembered to
    // send it: checkout carried an explicit `lang` field, account deletion read
    // the BROWSER's Accept-Language (which is not the dashboard's), password
    // reset sent nothing, and the billing portal sent nothing. Four of this
    // round's defects are the same missing value in four places, so it is set
    // once, here, rather than added to a fifth caller.
    //
    // Added to the EXISTING request interceptor rather than registering a
    // second one. Accept-Language is CORS-safelisted, so it needs no entry in
    // the server's Access-Control-Allow-Headers even when the API is on another
    // origin, and it is not a forbidden header, so browsers let a script set it.
    // The server reads it in one place, `request_language()`.
    config.headers.set("Accept-Language", detectLanguage());

    // Add authentication token (uses cached token)
    const token = await tokenCache.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logger.warn("No authentication token available");
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ============================================================================
// Response Interceptor
// ============================================================================

api.interceptors.response.use(
  (response) => {
    // Record success for circuit breaker
    const service = response.config.baseURL
      ? new URL(response.config.baseURL).hostname
      : "unknown";
    recordSuccess(service);

    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryableRequestConfig;

    if (!config) {
      return Promise.reject(error);
    }

    const service = config.baseURL
      ? new URL(config.baseURL).hostname
      : "unknown";

    // Handle 401 Unauthorized - token refresh
    if (error.response?.status === 401 && !config._retry) {
      config._retry = true;

      // On public paths (cart, checkout, etc.), don't redirect - allow guest checkout
      if (isPublicPath()) {
        logger.debug(
          "401 on public path - allowing guest access without redirect",
        );
        return Promise.reject(error);
      }

      logger.warn("401 Unauthorized - attempting token refresh");

      /*
       * N1 (FCR S687): a failed renewal must not destroy the refresh token.
       *
       * This handler used to treat EVERY unsuccessful outcome the same way --
       * destroy both tokens and redirect. refreshToken() returns null for a
       * rejected refresh token, a service outage, a network failure, a rate
       * limit AND the thirty-second client throttle, and no caller could tell
       * them apart. shouldClearTokensForRefreshStatus() exists so a 500, a 503,
       * a 429 or an unreachable service leaves both tokens in place -- but that
       * rule protects the REFRESH path, not this one.
       *
       * The throttle case is the sharpest: a renewal fails with a 503, a second
       * request 401s within thirty seconds, the throttle answers null because
       * the access token has expired, and a perfectly good thirty-day credential
       * is destroyed by a rate limiter added to protect the service.
       *
       * renewSession() answers with the three cases instead. Only "signed-out"
       * -- 400/401/403, the credential is genuinely no good -- redirects.
       *
       * tokenCache.clearCache() is NOT called before this any more. It resets
       * renewalAttempted to false, which puts shouldHoldForRenewal() back into
       * "hold" and can hand the route guards a spinner while the request that
       * caused all this is still unwinding.
       *
       * There are TWO of these handlers -- this one and the other in the file
       * named below -- and fixing only one leaves the defect fully live. Check
       * them against each other whenever either changes; two copies of one rule
       * is what allowed this to be described as living in a single file.
       */
      // The sibling handler is in frontend/src/redux/api-config.ts.
      const outcome = await tokenCache.renewSession();

      if (outcome.status === "renewed") {
        config.headers.Authorization = `Bearer ${outcome.token}`;
        return api(config);
      }

      if (outcome.status === "signed-out") {
        // The credential is genuinely no good -- redirect to login (only for
        // protected routes). Issue 1 (FCR Pass 10): the shared helper clears the
        // token before redirecting and its same-origin guard prevents an open
        // redirect.
        logger.error("The session credential was rejected; redirecting to login");
        redirectToAuthLogin(window.location.href);
        return Promise.reject(error);
      }

      // "unavailable": keep the session, let the caller see the error, try
      // again later.
      logger.warn("Session renewal unavailable; keeping the session");
      announceRenewalUnavailable();
      return Promise.reject(error);
    }

    // Retry logic for transient errors
    if (isRetryableError(error)) {
      // N-8 (FCR S732): never repeat a request that changes something unless
      // the caller said it is safe to (see mayRetryAutomatically).
      if (!mayRetryAutomatically(config)) {
        recordFailure(service);
        return Promise.reject(error);
      }
      const retryCount = config._retryCount || 0;
      const delay = getRetryDelay(retryCount);

      // A-5: an interactive request stops when its budget is spent, even with
      // attempts left. The customer is told what happened while they are still
      // watching, instead of being held on "Sending..." for two minutes.
      if (isBudgetExhausted(config, delay)) {
        logger.warn(
          `Interactive request exceeded its ${INTERACTIVE_TOTAL_BUDGET_MS}ms budget after ` +
            `${retryCount} retr${retryCount === 1 ? "y" : "ies"} - surfacing the failure`,
        );
        recordFailure(service);
        return Promise.reject(error);
      }

      if (retryCount < (config.maxRetries ?? MAX_RETRIES)) {
        config._retryCount = retryCount + 1;

        logger.warn(
          `Request failed, retrying (${retryCount + 1}/${MAX_RETRIES}) in ${delay}ms`,
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }

      // All retries exhausted, record failure for circuit breaker
      recordFailure(service);
    }

    return Promise.reject(error);
  },
);


